import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { BrushZoomConfig, DatedGreeks, VolSkew, VolSkewConfig } from 'types';
import { DEFAULT_BRUSH_ZOOM_CONFIG } from 'config';

interface VolSkewContextValue {
  selectedSym: string;

  config: VolSkewConfig;
  updateConfig: (patch: Partial<VolSkewConfig>) => void;

  selectedDatedGreeks: DatedGreeks[];
  setSelectedDatedGreeks: Dispatch<SetStateAction<DatedGreeks[]>>;

  visibleChartLines: string[];
  setVisibleChartLines: Dispatch<SetStateAction<string[]>>;

  zoomConfig: BrushZoomConfig;
  setZoomConfig: Dispatch<SetStateAction<BrushZoomConfig>>;

  volSkewData: VolSkew[] | undefined;
  setVolSkewData: Dispatch<SetStateAction<VolSkew[] | undefined>>;

  chartData: VolSkew[];
  setChartData: Dispatch<SetStateAction<VolSkew[]>>;

  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;

  chartRef: MutableRefObject<HTMLDivElement | null>;

  isExpirationsSelectorOpen: boolean;
  setIsExpirationsSelectorOpen: Dispatch<SetStateAction<boolean>>;
}

const VolSkewContext = createContext<VolSkewContextValue | null>(null);

interface VolSkewProviderProps {
  selectedSym: string;
  config: VolSkewConfig;
  onConfigChange: (patch: Partial<VolSkewConfig>) => void;
  children: ReactNode;
}

export const VolSkewProvider = ({
  selectedSym,
  config,
  onConfigChange,
  children,
}: VolSkewProviderProps) => {
  const [isExpirationsSelectorOpen, setIsExpirationsSelectorOpen] =
    useState<boolean>(false);

  // Starts empty; the chart's load pipeline is the single source of truth that
  // seeds/hydrates lines from `config` (starting empty avoids racing it).
  const [selectedDatedGreeks, setSelectedDatedGreeks] = useState<DatedGreeks[]>(
    [],
  );
  const [visibleChartLines, setVisibleChartLines] = useState<string[]>([]);
  const [zoomConfig, setZoomConfig] = useState<BrushZoomConfig>(
    DEFAULT_BRUSH_ZOOM_CONFIG,
  );
  const [volSkewData, setVolSkewData] = useState<VolSkew[] | undefined>(
    undefined,
  );
  const [chartData, setChartData] = useState<VolSkew[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);

  // Lines persist whole, so config updates pass straight through to the host.
  const updateConfig = onConfigChange;

  return (
    <VolSkewContext.Provider
      value={{
        selectedSym,
        config,
        updateConfig,
        selectedDatedGreeks,
        setSelectedDatedGreeks,
        visibleChartLines,
        setVisibleChartLines,
        zoomConfig,
        setZoomConfig,
        volSkewData,
        setVolSkewData,
        chartData,
        setChartData,
        loading,
        setLoading,
        error,
        setError,
        chartRef,
        isExpirationsSelectorOpen,
        setIsExpirationsSelectorOpen,
      }}
    >
      {children}
    </VolSkewContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useVolSkewCtx = (): VolSkewContextValue => {
  const ctx = useContext(VolSkewContext);
  if (ctx == null) {
    throw new Error('useVolSkewCtx must be used within a VolSkewProvider');
  }
  return ctx;
};
